/**
 * @file useStatementRunWork.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-19
 *
 * A statement run's progress as the server holds it — so a run can be handed
 * over or shared and everyone on it sees the same step, contacts, bundling and
 * delivery choices (services/statementRunWorkService.js).
 *
 *   const runWork = useStatementRunWork(runId);
 *   runWork.work      // { ready, runId, version, values, people, lastChange, … } or null
 *   runWork.save({ destination: 'print' })          // → { ok } | { ok: false, code, error, work }
 *   runWork.save({ excludedInvoices: { add: [17] } })
 *   runWork.confirmAccounts(true)   // the Accounts confirmation (1.2.0) → { ok } | { ok: false, code, error }
 *
 * Saves go one at a time, each with the version the last one returned, so a
 * screen never trips over its own changes; a colleague's newer change to the
 * same thing comes back as 409 RUN_CHANGED with the run as it now is. The run
 * is re-read every `pollMs` while the tab is visible, so a colleague's changes
 * appear. Before the server's migration has run, `work.ready` is false and
 * save() does nothing — the screen works as it always did.
 *
 * An answer older than the run already held (a slow poll overtaken by a save)
 * is ignored. Polls say which version they hold, and the server leaves the
 * contacts out when that has not moved; they are kept from the held copy. The
 * first read is retried (2s, 4s, 8s … 30s) until it answers.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';

const same = (a, b) => a != null && b != null && String(a) === String(b);

export default function useStatementRunWork(runId, { pollMs = 15000 } = {}) {
  const [work, setWorkState] = useState(null);
  const workRef = useRef(null);
  const runIdRef = useRef(runId);
  runIdRef.current = runId;
  const chainRef = useRef(Promise.resolve());

  // Keep the ref current at once (saves chained behind this one read it).
  const setWork = useCallback((w) => {
    workRef.current = w;
    setWorkState(w);
  }, []);

  /**
   * Take an answer for run `id`: ignored when it is older than the copy held
   * for the same run; the contacts filled in from the held copy when the
   * server left them out. Returns what is now held (or the answer, for a run
   * the screen has moved off).
   */
  const adopt = useCallback((w, id) => {
    if (!w) return null;
    const held = workRef.current;
    const sameRun = held && held.ready && !held.missing && Number(held.runId) === Number(w.runId);
    let next = w;
    if (sameRun && w.ready && Number(w.version) < Number(held.version)) return held;
    if (w.contactsOmitted && sameRun && held.values && 'contacts' in held.values) {
      next = { ...w, values: { ...w.values, contacts: held.values.contacts } };
    }
    if (same(runIdRef.current, id)) setWork(next);
    else workRef.current = next;
    return next;
  }, [setWork]);

  const fetchWork = useCallback(async (id, since) => {
    try {
      const q = since !== undefined && since !== null ? `?since=${encodeURIComponent(since)}` : '';
      const res = await fetch(`${API_URL}/api/statements/work/${id}${q}`, { credentials: 'include' });
      if (res.status === 404) return { ready: true, missing: true, runId: Number(id) };
      if (!res.ok) return null;
      const data = await res.json();
      return data && data.ready === true ? data : { ready: false, runId: Number(id) };
    } catch (_) {
      return null;
    }
  }, []);

  /** Re-read the run now (saying which version is held, so unchanged contacts are not resent). */
  const reload = useCallback(async () => {
    const id = runIdRef.current;
    if (!id) return null;
    const held = workRef.current;
    const since = held?.ready && !held.missing && Number(held.runId) === Number(id) && held.values && 'contacts' in held.values
      ? held.version : undefined;
    const w = await fetchWork(id, since);
    return adopt(w, id);
  }, [fetchWork, adopt]);

  useEffect(() => {
    setWork(null);
    if (!runId) return undefined;
    // The first read, retried with a growing wait until the server answers.
    let cancelled = false;
    let timer = null;
    let wait = 2000;
    const attempt = async () => {
      const w = await reload();
      if (cancelled || w) return;
      timer = setTimeout(attempt, wait);
      wait = Math.min(wait * 2, 30000);
    };
    attempt();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [runId, reload, setWork]);

  useEffect(() => {
    if (!runId || !pollMs) return undefined;
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (workRef.current?.ready && !workRef.current?.missing) reload();
    }, pollMs);
    return () => clearInterval(timer);
  }, [runId, pollMs, reload]);

  /**
   * Save changes to the run (one at a time). `opts.runId` names the run when
   * the screen has just moved to it and this hook has not re-rendered yet.
   */
  const save = useCallback((changes, opts = {}) => {
    const id = opts.runId || runIdRef.current;
    if (!id || !changes || !Object.keys(changes).length) return Promise.resolve(null);
    const p = chainRef.current.then(async () => {
      let w = workRef.current;
      if (!w || Number(w.runId) !== Number(id)) {
        w = adopt(await fetchWork(id), id);
      }
      if (!w?.ready || w.missing) return null;
      const res = await fetch(`${API_URL}/api/statements/work/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ baseVersion: w.version, changes })
      });
      const data = await res.json().catch(() => ({}));
      const held = data.work ? adopt(data.work, id) : null;
      if (res.ok) return { ok: true, work: held };
      return { ok: false, status: res.status, code: data.code || null, error: data.error || `HTTP ${res.status}`, work: held, supersededBy: data.supersededBy || null };
    }).catch(err => ({ ok: false, error: err.message }));
    chainRef.current = p.then(() => undefined, () => undefined);
    return p;
  }, [fetchWork, adopt]);

  /**
   * Tick or untick the run's Accounts confirmation. Chained behind any save
   * in flight, with the version held, so a colleague's newer change comes back
   * as RUN_CHANGED (the run as it now is is adopted).
   */
  const confirmAccounts = useCallback((confirmed) => {
    const id = runIdRef.current;
    if (!id) return Promise.resolve(null);
    const p = chainRef.current.then(async () => {
      const w = workRef.current;
      if (!w?.ready || w.missing || Number(w.runId) !== Number(id)) return { ok: false, error: 'The run has not loaded yet — try again.' };
      const res = await fetch(`${API_URL}/api/statements/work/${id}/accounts-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirmed: !!confirmed, baseVersion: w.version })
      });
      const data = await res.json().catch(() => ({}));
      if (data.work) adopt(data.work, id);
      if (res.ok) return { ok: true, changed: !!data.changed };
      return { ok: false, status: res.status, code: data.code || null, error: data.error || `HTTP ${res.status}` };
    }).catch(err => ({ ok: false, error: err.message }));
    chainRef.current = p.then(() => undefined, () => undefined);
    return p;
  }, [adopt]);

  /** Keep extra PDFs with the run. */
  const addAttachments = useCallback(async (files) => {
    const id = runIdRef.current;
    if (!id) return null;
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    const res = await fetch(`${API_URL}/api/statements/work/${id}/attachments`, { method: 'POST', body: fd, credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (data.work) adopt(data.work, id);
    return res.ok ? { ok: true } : { ok: false, error: data.error || `HTTP ${res.status}`, code: data.code };
  }, [adopt]);

  const removeAttachment = useCallback(async (attId) => {
    const id = runIdRef.current;
    if (!id) return null;
    const res = await fetch(`${API_URL}/api/statements/work/${id}/attachments/${encodeURIComponent(attId)}`, { method: 'DELETE', credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (data.work) adopt(data.work, id);
    return res.ok ? { ok: true } : { ok: false, error: data.error || `HTTP ${res.status}` };
  }, [adopt]);

  return {
    work,
    ready: work?.ready === true && !work?.missing,
    reload,
    save,
    setWork,
    adopt,
    confirmAccounts,
    addAttachments,
    removeAttachment
  };
}
